import * as React from "react";
import { cn } from "../../../lib/utils";

interface InputOTPContextValue {
  value: string;
  setValue: (value: string) => void;
  maxLength: number;
  inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  autoFocus?: boolean;
  autoComplete: string;
}

const InputOTPContext = React.createContext<InputOTPContextValue | null>(null);

interface InputOTPProps {
  maxLength?: number;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
  id?: string;
  autoFocus?: boolean;
  /**
   * "one-time-code" is what makes iOS/Android offer the emailed or texted code
   * above the keyboard. Pass "off" for codes the user chooses, such as a PIN.
   */
  autoComplete?: string;
}

const InputOTP = React.forwardRef<HTMLDivElement, InputOTPProps>(
  (
    {
      maxLength = 6,
      value,
      onChange,
      children,
      className,
      id,
      autoFocus = true,
      autoComplete = "one-time-code",
      ...props
    },
    ref,
  ) => {
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    const setValue = React.useCallback(
      (newValue: string) => {
        if (newValue.length <= maxLength) {
          onChange(newValue);
        }
      },
      [maxLength, onChange],
    );

    const contextValue: InputOTPContextValue = React.useMemo(
      () => ({
        value,
        setValue,
        maxLength,
        inputRefs,
        autoFocus,
        autoComplete,
      }),
      [value, setValue, maxLength, autoFocus, autoComplete],
    );

    return (
      <InputOTPContext.Provider value={contextValue}>
        <div ref={ref} className={cn("flex", className)} {...props}>
          {children}
        </div>
      </InputOTPContext.Provider>
    );
  },
);
InputOTP.displayName = "InputOTP";

interface InputOTPGroupProps {
  children: React.ReactNode;
  className?: string;
}

const InputOTPGroup = React.forwardRef<HTMLDivElement, InputOTPGroupProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("flex items-center", className)} {...props}>
        {children}
      </div>
    );
  },
);
InputOTPGroup.displayName = "InputOTPGroup";

interface InputOTPSlotProps extends React.ComponentProps<"input"> {
  index: number;
}

const InputOTPSlot = React.forwardRef<HTMLInputElement, InputOTPSlotProps>(
  ({ index, className, ...props }, ref) => {
    const context = React.useContext(InputOTPContext);
    if (!context) {
      throw new Error("InputOTPSlot must be used within InputOTP");
    }

    const {
      value,
      setValue,
      maxLength,
      inputRefs,
      autoFocus = true,
      autoComplete,
    } = context;
    const currentValue = value[index] || "";
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
      inputRefs.current[index] = inputRef.current;
    }, [index, inputRefs]);

    React.useEffect(() => {
      if (index !== 0 || !autoFocus || !inputRef.current || inputRef.current.disabled) {
        return undefined;
      }

      const timeoutId = window.setTimeout(() => {
        inputRef.current?.focus();
        try {
          inputRef.current?.setSelectionRange(0, 0);
        } catch {
          // ignore
        }
      }, 100);

      return () => window.clearTimeout(timeoutId);
    }, [index, autoFocus]);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const focusSlot = (target: number) => {
      inputRefs.current[Math.min(target, maxLength - 1)]?.focus();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, "");

      if (!digits) {
        const cleared = value.split("");
        cleared[index] = "";
        setValue(cleared.join("").slice(0, maxLength));
        return;
      }

      // iOS one-time-code autofill and the keyboard's Paste suggestion drop the
      // whole code into whichever box has focus. Spread it across the slots —
      // this used to be discarded, so autofill silently did nothing.
      if (digits.length >= maxLength) {
        setValue(digits.slice(0, maxLength));
        focusSlot(maxLength - 1);
        return;
      }

      // A box that already holds a digit keeps it in the DOM value, so typing
      // over it arrives as "old" + "new".
      const typed =
        currentValue && digits.startsWith(currentValue)
          ? digits.slice(currentValue.length)
          : digits;

      if (!typed) {
        return;
      }

      const slots = Array.from(
        { length: maxLength },
        (_unused, slot) => value[slot] || "",
      );
      typed.split("").forEach((digit, offset) => {
        if (index + offset < maxLength) {
          slots[index + offset] = digit;
        }
      });

      setValue(slots.join("").slice(0, maxLength));
      focusSlot(index + typed.length);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !currentValue && index > 0) {
        const prevInput = inputRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
        }
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData("text").slice(0, maxLength);
      if (/^\d+$/.test(pastedData)) {
        setValue(pastedData);
        focusSlot(pastedData.length);
      }
    };

    return (
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={autoComplete}
          autoCorrect="off"
          spellCheck={false}
          // A 1-char cap makes iOS truncate an autofilled code to nothing usable.
          // The rendered value is controlled per slot, so each box still shows
          // one digit while handleChange spreads the rest.
          maxLength={maxLength}
          data-otp-slot={index}
          value={currentValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-md border border-input bg-background text-center text-base ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);
InputOTPSlot.displayName = "InputOTPSlot";

export { InputOTP, InputOTPGroup, InputOTPSlot };
